import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Box, Button, Card, Column, IconButton, Row, Typography } from 'seagull';
import { Checkmark, Info } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { utils } from '~/utils';
import { billingScheduleSectionsTextsPath, makeSectionsFromServices } from '../../../../PackagesStep/makeSectionsFromServices';
import { BillingSection } from './BillingSection';
import styles from './PackageCard.module.scss';
export const GetPackageCardTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const PackageCard = (param)=>{
    let { package: pkg, isSelected, isEffectiveDateInFuture, canSelect, onSelect, onInfoClick, resolvedValues, testId, disabled, style } = param;
    const { t: packageCardTexts } = useLocaleKeys('clientFlow.packageCard');
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { t: sectionsTexts } = useLocaleKeys(billingScheduleSectionsTextsPath);
    const { PackageCardTestIds } = useMemo(()=>GetPackageCardTID(testId), [
        testId
    ]);
    const handleInfoClick = (e)=>{
        e.stopPropagation();
        onInfoClick === null || onInfoClick === void 0 ? void 0 : onInfoClick();
    };
    const sections = useMemo(()=>makeSectionsFromServices(pkg.services, {
            isEffectiveDateInFuture,
            costDisplayTexts,
            sectionsTexts
        }), [
        pkg.services,
        isEffectiveDateInFuture
    ]);
    const oneTimeSections = sections.filter((s)=>s.category === 'oneTime');
    const recurringSections = sections.filter((s)=>s.category === 'recurring');
    const variableSections = sections.filter((s)=>s.category === 'variable');
    const optionalSection = sections.find((s)=>s.category === 'optional');
    const handleCardClick = ()=>{
        if (!disabled && (canSelect || isSelected)) {
            onSelect();
        }
    };
    return /*#__PURE__*/ _jsx(Card, {
        bi: {
            name: 'select_package_card'
        },
        className: cn(styles.packageCard, {
            [styles.selected]: isSelected,
            [styles.disabled]: disabled && !isSelected
        }),
        testId: PackageCardTestIds.Root(),
        "data-bundleid": pkg.id,
        onClick: handleCardClick,
        role: "button",
        tabIndex: disabled ? -1 : 0,
        onKeyDown: (e)=>{
            if (e.target !== e.currentTarget) return;
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                e.preventDefault();
                handleCardClick();
            }
        },
        style: style,
        children: /*#__PURE__*/ _jsxs(Column, {
            className: styles.cardContent,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    className: styles.titleRow,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            weight: "normal",
                            className: styles.packageName,
                            testId: PackageCardTestIds.Title(),
                            children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                text: pkg.name,
                                resolvedValues: resolvedValues
                            })
                        }),
                        onInfoClick && /*#__PURE__*/ _jsx(Box, {
                            className: styles.infoButton,
                            children: /*#__PURE__*/ _jsx(IconButton, {
                                bi: {
                                    name: 'view_package_details'
                                },
                                skin: "transparent",
                                size: "medium",
                                "aria-label": packageCardTexts.viewDetails(),
                                tooltip: {
                                    title: packageCardTexts.viewDetails(),
                                    placement: 'top'
                                },
                                onClick: handleInfoClick,
                                testId: PackageCardTestIds.InfoButton(),
                                children: /*#__PURE__*/ _jsx(Info, {
                                    size: 24
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: styles.sectionsContainer,
                    testId: PackageCardTestIds.SectionsContainer(),
                    children: [
                        oneTimeSections.map((section)=>/*#__PURE__*/ _jsx(BillingSection, {
                                title: section.sum ? utils.formatDollarAmount(section.sum) : undefined,
                                subtitle: section.subtitle,
                                services: section.services,
                                resolvedValues: resolvedValues,
                                testId: "".concat(testId, "-section-").concat(section.id)
                            }, section.id)),
                        recurringSections.map((section)=>/*#__PURE__*/ _jsx(BillingSection, {
                                title: section.sum ? utils.formatDollarAmount(section.sum) : undefined,
                                subtitle: section.subtitle,
                                services: section.services,
                                resolvedValues: resolvedValues,
                                testId: "".concat(testId, "-section-").concat(section.id)
                            }, section.id)),
                        variableSections.map((section)=>/*#__PURE__*/ _jsx(BillingSection, {
                                title: packageCardTexts.variable(),
                                services: section.services,
                                resolvedValues: resolvedValues,
                                testId: "".concat(testId, "-section-").concat(section.id)
                            }, section.id)),
                        optionalSection && optionalSection.services.length > 0 && /*#__PURE__*/ _jsx(BillingSection, {
                            title: packageCardTexts.optionalServices(),
                            services: optionalSection.services,
                            isOptional: true,
                            resolvedValues: resolvedValues,
                            testId: "".concat(testId, "-section-optional")
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Row, {
                    className: styles.bottomArea,
                    children: canSelect && isSelected ? /*#__PURE__*/ _jsxs(Row, {
                        className: styles.selectedIndicator,
                        testId: PackageCardTestIds.SelectedIndicator(),
                        children: [
                            /*#__PURE__*/ _jsx(Checkmark, {}),
                            /*#__PURE__*/ _jsx(Typography, {
                                weight: "normal",
                                className: styles.selectedLabel,
                                children: "Selected"
                            })
                        ]
                    }) : canSelect ? /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'select_package'
                        },
                        intent: Intent.SelectPackage,
                        skin: "primaryOutlineOpaque",
                        onClick: (e)=>{
                            e.stopPropagation();
                            onSelect();
                        },
                        className: styles.selectButton,
                        testId: PackageCardTestIds.SelectButton(),
                        disabled: disabled,
                        children: "Select"
                    }) : /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'view_package'
                        },
                        skin: "primaryOutlineOpaque",
                        onClick: (e)=>{
                            e.stopPropagation();
                            onSelect();
                        },
                        className: styles.selectButton,
                        testId: PackageCardTestIds.ViewButton(),
                        disabled: disabled,
                        children: "View"
                    })
                })
            ]
        })
    });
};
